import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { COLORS } from './game-engine/config';
import { useGameController } from './hooks';
import { PlayButton, RecentBins } from './components';
import { StatsPanel } from './components/StatsPanel';
import { PlaceYourBet } from './components/PlaceYourBet';
import { ContainerShape, HexagonShape, InnerShape } from './components/Shape';
import { ProfileSlot } from '~/blinko/components/ProfileSlot';
import { useAccount } from 'wagmi';
import imgHoney from './assets/honey.png'
import { useAtomValue, useSetAtom } from 'jotai';
import {
  atomUiHidden,
  atomBoardReady,
  atomControlsReady,
  atomIsReplayMode
} from './states/game.atoms';
import { atomIsIgamingDemo } from './states';
import { BackgroundLayer } from './components/BackgroundLayer';
import { BoardLayer } from './components/BoardLayer';
import { useAssets } from './components/AssetHandler';
import { Menu } from './components/Menu';
import { YatsuMuteBridge } from './components/YatsuMuteBridge';
import { MetadataSlot } from './components/MetadataHandler';
import { LeaderboardModal } from './modals/leaderboard.modal';
import { useSound } from './components/SoundHandler';
import { useResolution } from './hooks/useResolution';
import { useModal } from '~/core/contexts/ModalContext';
import { icons } from './assets/icons/icons';
import { ReferralCode } from './components/ReferralCode';
import { ShopModal } from './modals/shop.modal';
import { formatNumber } from '~/core/utils/formatNumber';

import { GameActionsProvider } from './contexts/GameActionsContext';

interface BlinkoGameProps {
  isIgamingDemo?: boolean;
}

const BlinkoGame: React.FC<BlinkoGameProps> = ({ isIgamingDemo = false }) => {
  const { address } = useAccount()
  const { isMobile } = useResolution()
  const sound = useSound()
  const { showModal } = useModal()

  const { canvasRef: controllerCanvasRef, canvasDimensions, actions } = useGameController({ isIgamingDemo });

  // Atoms for UI
  const uiHidden = useAtomValue(atomUiHidden);
  
  // Loading Coordination
  const { markAssetsReady } = useAssets();
  const boardReady = useAtomValue(atomBoardReady);
  const controlsReady = useAtomValue(atomControlsReady);

  useEffect(() => {
    if (boardReady && controlsReady) {
      markAssetsReady();
    }
  }, [boardReady, controlsReady, markAssetsReady]);

  const onLeaderboard = () => {
    showModal(hide => <LeaderboardModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
  }

  const onShop = () => {
    showModal(hide => <ShopModal onDismiss={hide} />, { hideOnEscapeKey: true });
    sound.play('click');
  }

  const setIsReplayMode = useSetAtom(atomIsReplayMode);
  const setIsIgamingDemoAtom = useSetAtom(atomIsIgamingDemo);

  useEffect(() => {
    setIsReplayMode(false);
  }, [setIsReplayMode]);

  useEffect(() => {
    setIsIgamingDemoAtom(isIgamingDemo);
    return () => setIsIgamingDemoAtom(false);
  }, [isIgamingDemo, setIsIgamingDemoAtom]);

  return (
    <GameActionsProvider actions={actions}>
      {isIgamingDemo && <YatsuMuteBridge />}
      <div style={{
        backgroundColor: COLORS.background,
        minHeight: '100dvh',
      }}>
      {/* Global Logic Components (renderless or strictly logic binding) */}
     
      <div className='fixed inset-0 overflow-hidden' style={isIgamingDemo ? { right: 300 } : undefined}>
        <BackgroundLayer />

        <motion.div
          animate={{ opacity: uiHidden ? 0 : 1 }}
          transition={{ duration: 0.5 }}
        >
          <BoardLayer />

          <canvas
            ref={controllerCanvasRef}
            width={canvasDimensions.width}
            height={canvasDimensions.height}
            className="absolute inset-0 z-20"
          />
        </motion.div>
      </div>

      {isIgamingDemo && <StatsPanel />}

      <div className='absolute inset-0 overflow-hidden' style={isIgamingDemo ? { right: 300 } : undefined}>
        <motion.div
          initial={false}
          animate={{ opacity: uiHidden ? 0 : 1 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          style={{ pointerEvents: uiHidden ? 'none' : 'auto' }}
        >
          <div className={`absolute left-0 bottom-0 pl-4 ${address ? 'pb-[140px]' : 'pb-[140px]'} md:pl-8 md:pb-10`}>
            {!isIgamingDemo && (
              <img src={imgHoney} className='absolute right-4 bottom-2 w-[100px] hidden md:block'/>
            )}
            <div className='relative'>
              <RecentBins />
            </div>
          </div>

          <div className='pointer-events-none absolute left-0 md:left-auto right-0 bottom-0 px-4 py-4 md:px-8 md:py-10'>
            <div className={`flex items-center justify-end ${address ? '-mb-5' : ''} -mr-5 md:m-0 md:justify-center`}> 
              <div className='pointer-events-auto'>
                <PlayButton />
              </div>
            </div>
            
            {!isIgamingDemo && (
              <div className='pointer-events-auto'>
                <PlaceYourBet />
              </div>
            )}
          </div>

          {isIgamingDemo ? null : isMobile ? (
            <div className='absolute top-0 left-0 w-full px-2.5 py-6'>
              <Menu />
            </div>
          ) : (
            <>
              <div className="absolute left-8 top-10 flex flex-col gap-3">
                <div onClick={onLeaderboard}>
                  <MetadataSlot>
                    {({ leaderboardMetadata }) => leaderboardMetadata ? (
                      <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
                        <ProfileSlot address={address}>
                          {({ profile }) => (
                            <div className="flex items-center gap-3 p-4">
                              <HexagonShape cornerRadius={6} strokeWidth={1}>
                                <div className='w-10 h-10 p-2'>
                                  <img src={leaderboardMetadata.image} />
                                </div>
                              </HexagonShape>
                              <div className="flex flex-col mr-10 gap-1">
                                <p className="text-left text-[10px] font-blinko text-white/80">
                                  Leaderboard place:
                                </p>
                                <div className="flex items-center relative gap-2">
                                  <p className="text-base font-blinko text-white">
                                    {leaderboardMetadata.league === 'None' ? '-' : leaderboardMetadata.rank}
                                  </p>
                                  <div className="rounded" style={{ border: '1px solid #FFF085'}}>
                                    <div className="px-2 py-0.5 bg-[#F0B10080]">
                                      <p className="text-xs font-semibold text-white">
                                        {leaderboardMetadata.title}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                                <button onPointerDown={(e) => {
                                e.stopPropagation()
                                onShop()
                              }}>
                                <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                                  <div className='flex justify-center items-center gap-2 px-4 py-2'>
                                    <img src={icons.jars} className='w-4 h-4' />
                                    <p className="font-blinko text-sm text-white">{formatNumber(profile.totalHoney)}</p>
                                  </div>
                                </InnerShape>
                              </button>
                            </div>
                          )}
                        </ProfileSlot>
                      </ContainerShape>
                    ) : (
                        <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                          {isMobile ? (
                            <div className="relative p-3">
                              <img src={icons.cup} className='w-5 h-5' />
                              <div className='absolute inset-0 flex items-center justify-center'>
                                <p className="text-base font-blinko text-white">Leaderboard</p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-center items-center gap-3 px-8 py-3">
                              <img src={icons.cup} className='w-5 h-5' />
                              <p className="text-base font-blinko text-white">Leaderboard</p>
                            </div>
                          )}
                      </InnerShape>
                    )}
                  </MetadataSlot>
                </div>

                <div className='self-start'>
                  <ReferralCode />
                </div>
              </div>

              <div className='absolute right-4 top-6 md:right-8 md:top-10'>
                <Menu />
              </div>
            </>
          )}
        </motion.div>
      </div>
      </div>
    </GameActionsProvider>
  );
};

export default BlinkoGame;
